/**
 * Galaxy Spiral Visualizer
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class GalaxyVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;
  private rotationVelocity = 0;
  private lastRenderTime?: number;
  private emaFrameMs = 16.7;
  private dynamicQuality = 1.0;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'galaxy',
    name: 'Galaxy Spiral',
    description: 'Spiral galaxy with frequency-based arms',
    category: 'artistic',
    defaultZoom: 1.0,
    supports3D: false,
    supportsAutoRotation: true,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): number | void {
    const {
      ctx, width, height, activeWaves, speed, time, deltaTime, colorMode = 'wave',
      zoom, pan, isAutoRotating, autoRotRef = 0,
      smoothedMusicData, isUploadedAudioPlaying, uploadedAudioData
    } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Deep space background
    ctx.fillStyle = '#000510';
    ctx.fillRect(0, 0, width, height);

    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      displayWaves = parsedData.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }

    if (displayWaves.length === 0) return autoRotRef;

    // Auto rotation with music reactivity
    let newAutoRot = autoRotRef;
    if (isAutoRotating) {
      const rotationSpeed = 0.15;

      if (smoothedMusicData && isUploadedAudioPlaying) {
        const energyInfluence = (smoothedMusicData.totalEnergy / 500) * 0.3;
        const targetVelocity = rotationSpeed + energyInfluence;
        this.rotationVelocity = this.rotationVelocity * 0.85 + targetVelocity * 0.15;
        newAutoRot += deltaTime * this.rotationVelocity;
      } else {
        newAutoRot += deltaTime * rotationSpeed;
        this.rotationVelocity = rotationSpeed;
      }
    }

    const galaxyRotation = (pan.x * 0.003) + newAutoRot;
    const numArms = Math.max(2, Math.min(8, displayWaves.length));

    // Calculate energy
    const totalEnergy = smoothedMusicData
      ? smoothedMusicData.totalEnergy / 10
      : displayWaves.reduce((sum, w) => sum + w.amp, 0);
    const avgFreq = smoothedMusicData
      ? (smoothedMusicData.bassEnergy * 0.5 + smoothedMusicData.midEnergy * 2 + smoothedMusicData.trebleEnergy * 5) / (smoothedMusicData.totalEnergy || 1)
      : (displayWaves.length > 0 ? displayWaves.reduce((sum, w) => sum + (w.freq * w.amp), 0) / totalEnergy : 1);

    const spiralTightness = 0.15 + (avgFreq * 0.02);
    
    // Base quality from canvas size and DPR
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
    const area = width * height * dpr;
    const baseQuality = Math.max(0.6, Math.min(1, 900000 / Math.max(1, area)));

    // Adaptive quality: EMA of frame time, target ~60 FPS
    if (this.lastRenderTime !== undefined && isFinite(time)) {
      const dt = Math.max(0, (time - this.lastRenderTime));
      const frameMs = Math.min(100, Math.max(1, dt * 1000));
      const alpha = 0.15;
      this.emaFrameMs = this.emaFrameMs * (1 - alpha) + frameMs * alpha;
      const targetMs = 16.7;
      const tolerance = 2.5;
      if (this.emaFrameMs > targetMs + tolerance) {
        this.dynamicQuality = Math.max(0.65, this.dynamicQuality - 0.03);
      } else if (this.emaFrameMs < targetMs - tolerance) {
        this.dynamicQuality = Math.min(1.0, this.dynamicQuality + 0.02);
      }
    }
    this.lastRenderTime = time;

    const quality = Math.max(0.65, Math.min(1, baseQuality * this.dynamicQuality));
    const particlesPerArm = Math.floor(120 * quality);
    const maxRadius = Math.min(width, height) * 0.45 * zoom;

    // Draw core (supermassive black hole)
    this.drawCore(ctx, cx, cy, totalEnergy, quality);

    // Draw spiral arms
    displayWaves.forEach((wave, armIndex) => {
      this.drawSpiralArm(
        ctx, wave, armIndex, numArms, particlesPerArm, maxRadius,
        spiralTightness, galaxyRotation, time, speed, cx, cy, colorMode, quality
      );
    });

    // Add distant stars
    this.drawStars(ctx, width, height, time, quality);

    ctx.shadowBlur = 0;
    return newAutoRot;
  }

  private drawCore(ctx: CanvasRenderingContext2D, cx: number, cy: number, energy: number, quality: number): void {
    const coreRadius = 15 + (energy * 0.3);
    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreRadius * 3);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.1, 'rgba(180, 120, 255, 0.9)');
    gradient.addColorStop(0.3, 'rgba(100, 50, 255, 0.6)');
    gradient.addColorStop(0.6, 'rgba(50, 20, 150, 0.3)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(cx, cy, coreRadius * 3, 0, Math.PI * 2);
    ctx.fill();

    // Accretion disk - skip if low quality
    if (quality > 0.5) {
      ctx.strokeStyle = 'rgba(255, 200, 100, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, coreRadius * 2, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  private drawSpiralArm(
    ctx: CanvasRenderingContext2D,
    wave: any,
    armIndex: number,
    numArms: number,
    particlesPerArm: number,
    maxRadius: number,
    spiralTightness: number,
    galaxyRotation: number,
    time: number,
    speed: number,
    cx: number,
    cy: number,
    colorMode: 'wave' | 'channel' = 'wave',
    quality: number = 1
  ): void {
    const armAngleOffset = (armIndex * 2 * Math.PI) / numArms;
    const wavePhaseOffset = (wave.phase * Math.PI) / 180;
    const effectiveFreq = wave.freq * speed;
    
    // Use helper to get color based on mode
    const waveColor = this.getWaveColor(wave, colorMode);

    // Draw particles
    for (let i = 0; i < particlesPerArm; i++) {
      const t = i / particlesPerArm;
      const radius = maxRadius * Math.pow(t, 0.8);
      const spiralAngle = spiralTightness * radius + armAngleOffset + galaxyRotation;
      const waveMotion = Math.sin(effectiveFreq * time * 0.15 + wavePhaseOffset + t * Math.PI * 2) * 0.04;
      const finalAngle = spiralAngle + waveMotion;

      const x = cx + Math.cos(finalAngle) * radius;
      const y = cy + Math.sin(finalAngle) * radius;

      const baseSize = 2 + (wave.amp * 0.03);
      const distanceFactor = Math.pow(1 - t, 0.5);
      const size = baseSize * distanceFactor;
      const pulse = 1 + Math.sin(effectiveFreq * time * 0.5 + t * Math.PI * 4) * 0.1;
      const finalSize = size * pulse;

      const color = this.getArmColor(wave, radius, maxRadius, waveColor);

      const blurAmount = quality < 0.8 ? (4 + (wave.amp * 0.05)) : (8 + (wave.amp * 0.1));
      ctx.shadowBlur = blurAmount;
      ctx.shadowColor = waveColor;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, finalSize, 0, Math.PI * 2);
      ctx.fill();

      // Bright stars
      if (i % 15 === 0 && t > 0.3) {
        ctx.fillStyle = `rgba(255, 255, 255, ${0.6 * (1 - t)})`;
        ctx.beginPath();
        ctx.arc(x, y, finalSize * 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw connecting curve (nebula effect) with smooth curves
    ctx.shadowBlur = quality > 0.8 ? 15 : 5;
    ctx.shadowColor = waveColor;
    ctx.strokeStyle = this.getArmColor(wave, 0, maxRadius, waveColor, 0.5);
    ctx.lineWidth = 1.5;
    ctx.beginPath();

    // Helper to calculate point position
    const calcPoint = (index: number) => {
      const t = index / particlesPerArm;
      const radius = maxRadius * Math.pow(t, 0.8);
      const spiralAngle = spiralTightness * radius + armAngleOffset + galaxyRotation;
      const waveMotion = Math.sin(effectiveFreq * time * 0.15 + wavePhaseOffset + t * Math.PI * 2) * 0.04;
      const finalAngle = spiralAngle + waveMotion;
      return {
        x: cx + Math.cos(finalAngle) * radius,
        y: cy + Math.sin(finalAngle) * radius
      };
    };

    // Draw smooth curve using quadratic interpolation
    let prevPoint = calcPoint(0);
    ctx.moveTo(prevPoint.x, prevPoint.y);

    for (let i = 1; i < particlesPerArm; i++) {
      const curr = calcPoint(i);
      const next = i + 1 < particlesPerArm ? calcPoint(i + 1) : curr;
      
      // Control point for smooth curve (midpoint between current and next)
      const cpx = (curr.x + next.x) / 2;
      const cpy = (curr.y + next.y) / 2;
      
      ctx.quadraticCurveTo(curr.x, curr.y, cpx, cpy);
    }
    ctx.stroke();
  }

  private getArmColor(wave: any, distance: number, maxRadius: number, waveColor: string = '', ampMultiplier: number = 1): string {
    // Use provided waveColor or fallback to wave.color
    const color = waveColor || wave.color;
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    const distanceFade = Math.pow(1 - (distance / maxRadius), 1.5);
    const energyGlow = Math.min(1, (wave.amp / 100) * ampMultiplier);
    const alpha = distanceFade * 0.8 * energyGlow;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private drawStars(ctx: CanvasRenderingContext2D, width: number, height: number, time: number, quality: number = 1): void {
    ctx.shadowBlur = 0;
    const numStars = Math.floor(200 * quality);
    const starSeed = 12345;

    for (let i = 0; i < numStars; i++) {
      const x = ((starSeed * i * 7919) % width);
      const y = ((starSeed * i * 7727) % height);
      const twinkle = Math.sin(time * 2 + i * 0.1) * 0.5 + 0.5;
      const size = 0.5 + (i % 3) * 0.3;

      ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + twinkle * 0.4})`;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
